<template>
	<ui-root v-if="loaded" :data="region" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {Region} from "@t/region/Region";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {type NavigateItem, VIEWS_GROUP_ID} from "@t/ui/Menu.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {getIntAttParam} from "@/router/Router.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const region = ref<Region|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("RegionPage");
const frm = ref(null);

function receivedData(data:Region & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	region.value = data;
	loaded.value = true;
	if (region.value && region.value.id>0) {
		navStore.replace(region.value.name);
	}
}

function loadData() {
	const id = route.query.id;
	if (id > 0) {
		return env.net.fetch("api/regions/"+id+"?meta=true")
			.then((result) => result.json())
			.then(receivedData);
	}
	else {
		return env.net.fetch("api/regions/init?meta=true")
				.then((result) => result.json())
				.then(receivedData)
				.then(() =>{
					let zip = route.query.zip;
					if (zip) { // set default zip
						region.value.zip=zip;
					}
				});
	}
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/regions/"+region.value.id, "PUT", region.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/regions", "POST", region.value);
}

function sendData():Promise<NetworkResponse> {
	return (!region.value.id || region.value.id===0)?createData():saveData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});


watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => {
	mb.standardMenu();
	mb.inGroup(VIEWS_GROUP_ID, (gb) => {
		let rid = route.query.id;
		if (rid) {
			gb.navItem("filterRegionUsers", "filterRegionsObjects.do?obj=users&region="+ rid, "menu.filterRegionUsers", "user");
			gb.navItem("filterRegionCustomers", "filterRegionsObjects.do?obj=customers&region="+ rid, "filterRegionCustomers", "party");
		}
	});
});

onMounted(() => {
	navStore.forward(env.msg("menu.createRegion"));
});
</script>

<style>

</style>
